import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ClassificationGroup, MinimumInput } from '@core/generated/gql-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

type FixedLevelRow = { id: string; countryHash: string; rateDollars: string };

export interface FixedRatesTableProps {
  classificationGroups: ClassificationGroup[];
  clientRates: Array<{
    classificationLevelId?: string | null;
    minimums?: Array<{
      countryHash?: string | null;
      minimum?: number | null;
    }> | null;
  }>;
  onSave: (
    classificationLevelId: string,
    minimums: MinimumInput[],
  ) => Promise<{ success: boolean; error?: string }>;
  disabled?: boolean;
}

export const FixedRatesTable = ({
  classificationGroups,
  clientRates,
  onSave,
  disabled,
}: FixedRatesTableProps) => {
  const [levelRows, setLevelRows] = useState<Record<string, FixedLevelRow[]>>(
    {},
  );
  const [savingLevelId, setSavingLevelId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize rows only from saved client rates (setClassificationLevelClientRate)
  // - never from classification level template/minimums
  useEffect(() => {
    const next: Record<string, FixedLevelRow[]> = {};
    let rowId = 0;

    clientRates.forEach((rate) => {
      const levelId = rate.classificationLevelId || '';
      if (!levelId) return;

      const minimums = rate.minimums ?? [];

      next[levelId] = minimums
        .filter((m) => m?.countryHash != null && m?.countryHash !== '')
        .map((m) => ({
          id: `row-${levelId}-${rowId++}`,
          countryHash: m.countryHash ?? '',
          rateDollars: m?.minimum != null ? (m.minimum / 100).toFixed(2) : '',
        }));
    });

    setLevelRows(next);
  }, [clientRates]);

  const addRow = (levelId: string) => {
    setLevelRows((prev) => ({
      ...prev,
      [levelId]: [
        ...(prev[levelId] ?? []),
        {
          id: `row-${levelId}-${Date.now()}`,
          countryHash: '',
          rateDollars: '',
        },
      ],
    }));
  };

  const removeRow = (levelId: string, rowId: string) => {
    setLevelRows((prev) => ({
      ...prev,
      [levelId]: (prev[levelId] ?? []).filter((r) => r.id !== rowId),
    }));
  };

  const updateRow = (
    levelId: string,
    rowId: string,
    field: 'countryHash' | 'rateDollars',
    value: string,
  ) => {
    setLevelRows((prev) => ({
      ...prev,
      [levelId]: (prev[levelId] ?? []).map((r) =>
        r.id === rowId ? { ...r, [field]: value } : r,
      ),
    }));
  };

  const handleSave = async (levelId: string) => {
    const rows = levelRows[levelId] ?? [];
    if (rows.length === 0) {
      setErrors((e) => ({
        ...e,
        [levelId]: 'Add at least one country hash and charge rate',
      }));
      return;
    }

    const minimums: MinimumInput[] = [];
    for (const row of rows) {
      const ch = row.countryHash.trim();
      if (!ch) {
        setErrors((e) => ({
          ...e,
          [levelId]: 'Country hash is required for each row',
        }));
        return;
      }
      const parsed = parseFloat(row.rateDollars.trim());
      if (Number.isNaN(parsed) || parsed < 0) {
        setErrors((e) => ({
          ...e,
          [levelId]: 'Enter valid amount (e.g. 10.50) for each row',
        }));
        return;
      }
      minimums.push({ countryHash: ch, minimum: Math.round(parsed * 100) });
    }

    setErrors((e) => {
      const next = { ...e };
      delete next[levelId];
      return next;
    });
    setSavingLevelId(levelId);

    const result = await onSave(levelId, minimums);
    setSavingLevelId(null);

    if (result.success) {
      toast.success('Fixed rates saved');
    } else if (result.error) {
      setErrors((e) => ({ ...e, [levelId]: result.error! }));
      toast.error(result.error);
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
              Classification Group
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
              Classification
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
              Classification Level
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
              Country Hash
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
              Charge Rate ($)
            </th>
            <th className="w-32 border border-gray-300 px-4 py-2 text-left font-semibold">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {classificationGroups.flatMap((group) => {
            const groupLevels = (group.classifications ?? []).flatMap(
              (classification) =>
                (classification.classificationLevels ?? []).map((level) => ({
                  group,
                  classification,
                  level,
                })),
            );
            const groupRowSpan = groupLevels.reduce(
              (acc, { level }) =>
                acc + Math.max(1, levelRows[level.id || '']?.length ?? 0),
              0,
            );

            return groupLevels.flatMap(
              ({ group, classification, level }, levelIdx) => {
                const levelId = level.id || '';
                const rows = levelRows[levelId] ?? [];
                const rowCount = Math.max(1, rows.length);
                const isSaving = savingLevelId === levelId;
                const err = errors[levelId];
                const isFirstInGroup = levelIdx === 0;
                const classLevelIds = (
                  classification.classificationLevels ?? []
                ).map((classificationLevel) => classificationLevel.id || '');
                const classRowSpan = classLevelIds.reduce(
                  (acc, id) => acc + Math.max(1, levelRows[id]?.length ?? 0),
                  0,
                );
                const isFirstInClass =
                  (classification.classificationLevels ?? [])[0]?.id ===
                  level.id;

                if (rows.length > 0) {
                  return rows.map((row, idx) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      {isFirstInGroup && idx === 0 && (
                        <td
                          className="border border-gray-300 px-4 py-2 align-top font-semibold"
                          rowSpan={groupRowSpan}
                        >
                          {group.name || '-'}
                        </td>
                      )}
                      {isFirstInClass && idx === 0 && (
                        <td
                          className="border border-gray-300 px-4 py-2 pl-8 align-top"
                          rowSpan={classRowSpan}
                        >
                          {classification.name || '-'}
                        </td>
                      )}
                      {idx === 0 && (
                        <td
                          className="border border-gray-300 px-4 py-2 pl-16 align-top"
                          rowSpan={rows.length}
                        >
                          <div className="flex flex-col gap-1">
                            {level.name || '-'}
                            {err && (
                              <span className="text-xs text-red-600">
                                {err}
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="border border-gray-300 px-4 py-2 pl-24 align-top">
                        <input
                          type="text"
                          placeholder="e.g. AU"
                          disabled={isSaving || disabled}
                          className="w-24 rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-50"
                          value={row.countryHash}
                          onChange={(e) =>
                            updateRow(
                              levelId,
                              row.id,
                              'countryHash',
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td className="border border-gray-300 px-4 py-2 pl-24 align-top">
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="Amount"
                          disabled={isSaving || disabled}
                          className="w-24 rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-50"
                          value={row.rateDollars}
                          onChange={(e) =>
                            updateRow(
                              levelId,
                              row.id,
                              'rateDollars',
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td className="border border-gray-300 px-4 py-2 align-top">
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => addRow(levelId)}
                            disabled={isSaving || disabled}
                            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-blue-600 disabled:opacity-50"
                            title="Add rate row"
                          >
                            <FontAwesomeIcon
                              icon={faPlus}
                              className="h-4 w-4"
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeRow(levelId, row.id)}
                            disabled={isSaving || disabled || rows.length <= 1}
                            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
                            title="Remove row"
                          >
                            <FontAwesomeIcon
                              icon={faTrash}
                              className="h-4 w-4"
                            />
                          </button>
                          {idx === 0 && (
                            <button
                              type="button"
                              onClick={() => handleSave(levelId)}
                              disabled={isSaving || disabled}
                              className="rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ));
                }

                return (
                  <tr
                    key={`${group.id}-${classification.id}-${level.id}-empty`}
                  >
                    {isFirstInGroup && (
                      <td
                        className="border border-gray-300 px-4 py-2 align-top font-semibold"
                        rowSpan={groupRowSpan}
                      >
                        {group.name || '-'}
                      </td>
                    )}
                    {isFirstInClass && (
                      <td
                        className="border border-gray-300 px-4 py-2 pl-8 align-top"
                        rowSpan={classRowSpan}
                      >
                        {classification.name || '-'}
                      </td>
                    )}
                    <td className="border border-gray-300 px-4 py-2 pl-16 align-top">
                      <div className="flex flex-col gap-1">
                        {level.name || '-'}
                        {err && (
                          <span className="text-xs text-red-600">{err}</span>
                        )}
                      </div>
                    </td>
                    <td className="border border-gray-300 px-4 py-2 pl-24 align-top">
                      -
                    </td>
                    <td className="border border-gray-300 px-4 py-2 pl-24 align-top">
                      -
                    </td>
                    <td className="border border-gray-300 px-4 py-2 align-top">
                      <button
                        type="button"
                        onClick={() => addRow(levelId)}
                        disabled={isSaving || disabled}
                        className="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-sm hover:bg-gray-50 disabled:opacity-50"
                      >
                        <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                        Add rate
                      </button>
                    </td>
                  </tr>
                );
              },
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
